// Shrink an oversize PDF by re-drawing each page as a JPEG.
//
// Why (Jill 2026-09-09): a hotel receipt scanned to PDF arrived at 14 MB and
// rode into her expense report, which came out at 22 MB. The email function
// died with "Memory limit exceeded" and the submit failed twice. Receipts are
// pictures of paper; a 1600 px JPEG of each page reads fine and is a fraction
// of the size. A PDF under the threshold is returned untouched, and anything
// that can't be rendered is returned untouched too, so a receipt is never lost
// to the shrinker.
//
// Browser only (canvas). Node callers must not import this.
import { loadPdfjs, PDFJS_WASM_URL } from "./pdfjsLoader";
import { makeLazyLoader } from "./lazyImport";

const loadPdfLib = makeLazyLoader(
  () => import("pdf-lib"),
  "The attachment converter couldn't download over this connection."
);

// A receipt PDF bigger than this gets re-drawn. Phone scans of one page are
// well under it; anything over is high-resolution images that don't need to be.
export const SHRINK_THRESHOLD_BYTES = 2 * 1024 * 1024;
const MAX_PX = 1600;
const JPEG_QUALITY = 0.8;
const MAX_PAGES = 30;

/**
 * bytes: Uint8Array of a PDF. Returns { bytes, shrunk, pages }.
 * shrunk is false when the file was already small enough or could not be
 * re-drawn; in both cases bytes is the input, unchanged.
 */
export async function shrinkPdfBytes(bytes, { threshold = SHRINK_THRESHOLD_BYTES } = {}) {
  if (!bytes || bytes.byteLength <= threshold) return { bytes, shrunk: false, pages: null };
  if (typeof document === "undefined") return { bytes, shrunk: false, pages: null };
  try {
    const pdfjs = await loadPdfjs();
    // pdf.js takes ownership of the buffer it is handed; give it a copy so the
    // original stays intact for the untouched fallback.
    const src = await pdfjs.getDocument({ data: bytes.slice(), wasmUrl: PDFJS_WASM_URL }).promise;
    const count = Math.min(src.numPages, MAX_PAGES);
    if (!count || src.numPages > MAX_PAGES) { await src.destroy?.(); return { bytes, shrunk: false, pages: src.numPages || null }; }
    const { PDFDocument } = await loadPdfLib();
    const out = await PDFDocument.create();
    for (let i = 1; i <= count; i++) {
      const page = await src.getPage(i);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.min(4, MAX_PX / Math.max(base.width, base.height));
      const vp = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(vp.width));
      canvas.height = Math.max(1, Math.round(vp.height));
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport: vp }).promise;
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
      if (!blob) throw new Error("canvas.toBlob returned null");
      const jpeg = await out.embedJpg(new Uint8Array(await blob.arrayBuffer()));
      // keep the page's own paper size (points), image drawn edge to edge
      const p = out.addPage([base.width, base.height]);
      p.drawImage(jpeg, { x: 0, y: 0, width: base.width, height: base.height });
      page.cleanup?.();
    }
    await src.destroy?.();
    const shrunkBytes = await out.save();
    // Only keep the redraw if it actually helped.
    if (shrunkBytes.byteLength >= bytes.byteLength) return { bytes, shrunk: false, pages: count };
    return { bytes: shrunkBytes, shrunk: true, pages: count };
  } catch (e) {
    console.warn("PDF shrink skipped:", e?.message || e);
    return { bytes, shrunk: false, pages: null };
  }
}

export function formatMb(n) {
  return `${(n / (1024 * 1024)).toFixed(n >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}
